/**
 * BaseRepository - Common database operations for all repositories
 * Provides standardized CRUD operations and query building
 */

import {
  collection,
  addDoc,
  getDoc,
  getDocFromServer,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  runTransaction
} from 'firebase/firestore';
import { db } from '../../../firebase';
import {
  dynamicDomainListConstraints,
  isDynamicDomainCollectionName,
} from '../../domain/dynamicDomainQueries';

export class BaseRepository {
  constructor(collectionName, options = {}) {
    this.collectionName = collectionName;
    this.isHierarchical = options.isHierarchical || false;
    this.domainId = options.domainId || null;
    this.isDynamicDomainCollection = isDynamicDomainCollectionName(collectionName);

    // For non-hierarchical collections, use flat path
    if (!this.isHierarchical) {
      this.collectionRef = collection(db, collectionName);
    }
    // For hierarchical collections, collectionRef will be created per-operation with schoolId
  }

  /**
   * Get collection reference for hierarchical collections
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {CollectionReference} Collection reference
   */
  getCollectionRef(schoolId) {
    console.log(`[BaseRepository] getCollectionRef called`, {
      collectionName: this.collectionName,
      isHierarchical: this.isHierarchical,
      schoolId
    });

    if (this.isHierarchical) {
      if (!schoolId) {
        const error = `schoolId is required for hierarchical collection: ${this.collectionName}`;
        console.error(`[BaseRepository] ❌ ${error}`);
        throw new Error(error);
      }
      const ref = collection(db, 'schools', schoolId, this.collectionName);
      console.log(`[BaseRepository] ✅ Created hierarchical ref: ${ref.path}`);
      return ref;
    }
    console.log(`[BaseRepository] ✅ Using flat collection ref`);
    return this.collectionRef;
  }

  getListIdentityConstraints(schoolId) {
    return dynamicDomainListConstraints({
      collectionName: this.collectionName,
      schoolId,
      domainId: this.domainId,
    });
  }

  /**
   * Validate hierarchical operation parameters
   * @param {string} schoolId - School ID to validate
   * @param {string} operation - Operation being performed (for error messages)
   * @param {string} context - Additional context (optional)
   * @throws {Error} If validation fails
   */
  validateHierarchicalOperation(schoolId, operation, context = '') {
    if (this.isHierarchical && !schoolId) {
      const message = `schoolId is required for ${operation} on hierarchical collection: ${this.collectionName}${context ? ` (${context})` : ''}`;
      console.error(`[BaseRepository] ❌ ${message}`);
      throw new Error(message);
    }
  }

  /**
   * Create a new document with timestamps
   * @param {Object} data - Document data (must include schoolId for hierarchical collections)
   * @returns {Promise<Object>} Created document with ID
   */
  async create(data) {
    try {
      // Validate hierarchical operation
      this.validateHierarchicalOperation(data.schoolId, 'create', 'document creation');

      const documentData = {
        ...data,
        createdAt: data.createdAt || Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Get appropriate collection reference
      const collectionRef = this.getCollectionRef(data.schoolId);
      const docRef = await addDoc(collectionRef, documentData);

      return {
        id: docRef.id,
        ...documentData
      };
    } catch (error) {
      console.error(`Error creating document in ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Create one named document, returning the existing record on a retry.
   * @param {Object} data - Document data.
   * @param {string} id - Stable document ID.
   * @returns {Promise<Object>} Persisted record and creation outcome.
   */
  async createWithId(data, id) {
    this.validateHierarchicalOperation(
      data.schoolId,
      'createWithId',
      `document ID: ${id}`,
    );
    const documentData = {
      ...data,
      createdAt: data.createdAt || Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const documentRef = this.isHierarchical
      ? doc(db, 'schools', data.schoolId, this.collectionName, id)
      : doc(db, this.collectionName, id);

    return runTransaction(db, async transaction => {
      const existing = await transaction.get(documentRef);
      if (existing.exists()) {
        return {
          id,
          ...existing.data(),
          wasCreated: false,
        };
      }
      transaction.set(documentRef, documentData);
      return {
        id,
        ...documentData,
        wasCreated: true,
      };
    });
  }

  /**
   * Get a single document by ID
   * @param {string} id - Document ID
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object|null>} Document data with ID or null
   */
  async getById(id, schoolId = null) {
    try {
      // Validate hierarchical operation
      this.validateHierarchicalOperation(schoolId, 'getById', `document ID: ${id}`);

      let docRef;
      if (this.isHierarchical) {
        docRef = doc(db, 'schools', schoolId, this.collectionName, id);
      } else {
        docRef = doc(db, this.collectionName, id);
      }

      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      }

      return null;
    } catch (error) {
      console.error(`Error getting document ${id} from ${this.collectionName}:`, error);
      throw error;
    }
  }

  async getByIdFromServer(id, schoolId = null) {
    this.validateHierarchicalOperation(
      schoolId,
      'getByIdFromServer',
      `document ID: ${id}`,
    );
    const documentRef = this.isHierarchical
      ? doc(db, 'schools', schoolId, this.collectionName, id)
      : doc(db, this.collectionName, id);
    const snapshot = await getDocFromServer(documentRef);
    return snapshot.exists()
      ? {id: snapshot.id, ...snapshot.data()}
      : null;
  }

  /**
   * Get all documents from collection
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Array of documents with IDs
   */
  async getAll(schoolId = null) {
    try {
      console.log(`[BaseRepository] getAll() called for collection: ${this.collectionName}`);
      console.log(`[BaseRepository] isHierarchical: ${this.isHierarchical}`);
      console.log(`[BaseRepository] schoolId: ${schoolId}`);

      const collectionRef = this.getCollectionRef(schoolId);
      console.log(`[BaseRepository] Collection ref path:`, collectionRef.path);

      console.log(`[BaseRepository] About to call getDocs()...`);
      const identityConstraints = this.getListIdentityConstraints(schoolId);
      const listQuery = identityConstraints.length > 0
        ? query(collectionRef, ...identityConstraints)
        : collectionRef;
      const querySnapshot = await getDocs(listQuery);
      console.log(`[BaseRepository] ✅ getDocs() returned! Size: ${querySnapshot.size}`);

      const documents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log(`[BaseRepository] Returning ${documents.length} documents`);

      return documents;
    } catch (error) {
      console.error(`[BaseRepository] ❌ Error getting documents from ${this.collectionName}:`, error);
      console.error(`[BaseRepository] Error details:`, {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  }

  /**
   * Update a document by ID
   * @param {string} id - Document ID
   * @param {Object} updates - Fields to update (must include schoolId for hierarchical collections)
   * @returns {Promise<Object>} Updated document data
   */
  async update(id, updates) {
    try {
      let docRef;
      let updateData;

      if (this.isHierarchical) {
        const schoolId = updates.schoolId;
        if (!schoolId) {
          throw new Error(`schoolId is required for hierarchical collection: ${this.collectionName}`);
        }
        docRef = doc(db, 'schools', schoolId, this.collectionName, id);

        // Remove schoolId from updates - it's used for routing, not data
        const dataWithoutSchoolId = { ...updates };
        delete dataWithoutSchoolId.schoolId;
        updateData = {
          ...dataWithoutSchoolId,
          updatedAt: Timestamp.now()
        };
      } else {
        docRef = doc(db, this.collectionName, id);
        updateData = {
          ...updates,
          updatedAt: Timestamp.now()
        };
      }

      await updateDoc(docRef, updateData);

      return {
        id,
        ...updateData
      };
    } catch (error) {
      console.error(`Error updating document ${id} in ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Delete a document by ID
   * @param {string} id - Document ID
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<void>}
   */
  async delete(id, schoolId = null) {
    try {
      let docRef;
      if (this.isHierarchical) {
        if (!schoolId) {
          throw new Error(`schoolId is required for hierarchical collection: ${this.collectionName}`);
        }
        docRef = doc(db, 'schools', schoolId, this.collectionName, id);
      } else {
        docRef = doc(db, this.collectionName, id);
      }
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document ${id} from ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Query documents with filters
   * @param {Array} constraints - Array of query constraints
   * @param {string} schoolId - School ID (required for hierarchical collections, pass as first argument or in constraints)
   * @returns {Promise<Array>} Array of matching documents
   */
  async queryDocuments(constraints = [], schoolId = null) {
    try {
      console.log(`[BaseRepository] 🔍 queryDocuments called for ${this.collectionName}`, {
        isHierarchical: this.isHierarchical,
        schoolId,
        constraintsCount: constraints.length
      });

      // Extract schoolId from constraints if provided as where clause
      if (this.isHierarchical && !schoolId) {
        const schoolIdConstraint = constraints.find(c =>
          c && c._field && c._field.segments && c._field.segments[0] === 'schoolId'
        );
        if (schoolIdConstraint && schoolIdConstraint._value) {
          schoolId = schoolIdConstraint._value;
          console.log(`[BaseRepository] 📌 Extracted schoolId from constraints:`, schoolId);
        }
      }

      console.log(`[BaseRepository] 🏫 About to get collection ref with schoolId:`, schoolId);
      const collectionRef = this.getCollectionRef(schoolId);
      console.log(`[BaseRepository] ✅ Got collection ref, building query...`);

      const q = query(
        collectionRef,
        ...this.getListIdentityConstraints(schoolId),
        ...constraints,
      );
      console.log(`[BaseRepository] 📖 Executing query on ${this.collectionName}...`);
      const querySnapshot = await getDocs(q);
      console.log(`[BaseRepository] ✅ Query complete: ${querySnapshot.size} documents found`);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`[BaseRepository] ❌ Error querying ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Create a real-time subscription to collection changes
   * @param {Function} callback - Callback for data changes
   * @param {Array} constraints - Query constraints (can be Firestore constraints or objects with {field, operator, value})
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback, constraints = [], schoolId = null) {
    try {
      console.log(`[BaseRepository] 🔔 subscribe() called for ${this.collectionName}`, {
        isHierarchical: this.isHierarchical,
        schoolId,
        constraintsCount: constraints.length
      });

      // Extract schoolId from constraints if provided
      if (this.isHierarchical && !schoolId) {
        const schoolIdConstraint = constraints.find(c =>
          (c && c._field && c._field.segments && c._field.segments[0] === 'schoolId') ||
          (c && c.field === 'schoolId')
        );
        if (schoolIdConstraint) {
          schoolId = schoolIdConstraint._value || schoolIdConstraint.value;
          console.log(`[BaseRepository] 🔔 Extracted schoolId from constraints:`, schoolId);
        }
      }

      // Convert constraint objects to Firestore query constraints
      const firestoreConstraints = constraints.map(constraint => {
        if (typeof constraint === 'object' && constraint.field) {
          // Object format: {field, operator, value}
          return where(constraint.field, constraint.operator, constraint.value);
        }
        // Already a Firestore constraint
        return constraint;
      });

      const collectionRef = this.getCollectionRef(schoolId);
      console.log(`[BaseRepository] 🔔 Collection ref path:`, collectionRef.path);

      const readConstraints = [
        ...this.getListIdentityConstraints(schoolId),
        ...firestoreConstraints,
      ];
      const q = readConstraints.length > 0
        ? query(collectionRef, ...readConstraints)
        : collectionRef;

      console.log(`[BaseRepository] 🔔 Setting up onSnapshot for ${this.collectionName}...`);

      return onSnapshot(
        q,
        (snapshot) => {
          console.log(`[BaseRepository] 🔔 onSnapshot FIRED for ${this.collectionName}! Size: ${snapshot.size}`);
          try {
            const data = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            console.log(`[BaseRepository] 🔔 Mapped ${data.length} documents, calling callback...`);
            callback(data);
          } catch (err) {
            console.error(`[BaseRepository] Error processing snapshot for ${this.collectionName}:`, err);
            callback([]);
          }
        },
        (error) => {
          console.error(`[BaseRepository] 🔴 Subscription error for ${this.collectionName}:`, error);
          console.error(`[BaseRepository] 🔴 Error details:`, {
            code: error.code,
            message: error.message,
            path: collectionRef.path
          });
          callback([]);
        }
      );
    } catch (error) {
      console.error(`[BaseRepository] CRITICAL: Error setting up subscription to ${this.collectionName}:`, error);
      callback([]);
      return () => {}; // Return no-op unsubscribe
    }
  }

  /**
   * Batch create multiple documents
   * @param {Array} documents - Array of document data
   * @returns {Promise<Array>} Array of created documents with IDs
   */
  async batchCreate(documents) {
    try {
      const promises = documents.map(data => this.create(data));
      return await Promise.all(promises);
    } catch (error) {
      console.error(`Error in batch create for ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Check if a document exists
   * @param {string} id - Document ID
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<boolean>} True if document exists
   */
  async exists(id, schoolId = null) {
    try {
      let docRef;
      if (this.isHierarchical) {
        if (!schoolId) {
          throw new Error(`schoolId is required for hierarchical collection: ${this.collectionName}`);
        }
        docRef = doc(db, 'schools', schoolId, this.collectionName, id);
      } else {
        docRef = doc(db, this.collectionName, id);
      }
      const docSnap = await getDoc(docRef);
      return docSnap.exists();
    } catch (error) {
      console.error(`Error checking existence of ${id} in ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Get all active rules for this collection.
   *
   * RuleService delegates here for any domain wired through
   * domainServiceFactory (academics, attendance, health, …). Discipline does
   * NOT use this path — RuleRepository overrides getActiveRules with its own
   * disciplineRules-specific query, so discipline behaviour is unchanged. This
   * generic implementation queries the repo's OWN collection, so each domain
   * reads its own rules collection (e.g. academicsRules).
   *
   * A single-field `active == true` filter needs no composite index; sortOrder
   * is applied client-side per the repo's "no multi-field orderBy" rule.
   *
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Active rules, sorted by sortOrder ascending
   */
  async getActiveRules(schoolId) {
    const rules = await this.queryDocuments([where('active', '==', true)], schoolId);
    return rules.sort((a, b) => (a.sortOrder ?? 999999) - (b.sortOrder ?? 999999));
  }

  /**
   * Get rules of a given type for this collection.
   * Generic counterpart to RuleRepository.getRulesByType — see getActiveRules.
   *
   * @param {string} type - Rule type to match
   * @param {boolean} activeOnly - Only return active rules
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Matching rules, sorted by sortOrder ascending
   */
  async getRulesByType(type, activeOnly = true, schoolId) {
    const constraints = [where('type', '==', type)];
    if (activeOnly) constraints.push(where('active', '==', true));
    const rules = await this.queryDocuments(constraints, schoolId);
    return rules.sort((a, b) => (a.sortOrder ?? 999999) - (b.sortOrder ?? 999999));
  }

  /**
   * Count documents matching constraints
   * @param {Array} constraints - Query constraints
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<number>} Count of matching documents
   */
  async count(constraints = [], schoolId = null) {
    try {
      const docs = await this.queryDocuments(constraints, schoolId);
      return docs.length;
    } catch (error) {
      console.error(`Error counting documents in ${this.collectionName}:`, error);
      throw error;
    }
  }
}

// Export query helper functions for use in repositories
export { where, orderBy, limit, Timestamp };
